import React from 'react';
import { CheckCircle, AlertTriangle, XCircle, Info, X } from 'lucide-react';

export interface AlertProps {
  variant?: 'success' | 'warning' | 'error' | 'info';
  title?: string;
  children: React.ReactNode;
  onDismiss?: () => void;
  icon?: React.ReactNode;
  className?: string;
}

const VARIANT_STYLES = {
  success: 'bg-emerald-400/[0.04] border-emerald-400/15 text-emerald-400',
  warning: 'bg-amber-400/[0.04] border-amber-400/15 text-amber-400',
  error: 'bg-red-400/[0.04] border-red-400/15 text-red-400',
  info: 'bg-blue-400/[0.04] border-blue-400/15 text-blue-400',
} as const;

const DEFAULT_ICONS = {
  success: CheckCircle,
  warning: AlertTriangle,
  error: XCircle,
  info: Info,
} as const;

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ variant = 'info', title, children, onDismiss, icon, className = '' }, ref) => {
    const variantStyle = VARIANT_STYLES[variant];
    const IconComponent = DEFAULT_ICONS[variant];
    const iconEl = icon ?? <IconComponent className="shrink-0 size-5" />;

    return (
      <div
        ref={ref}
        className={`flex items-start gap-3 p-4 rounded-xl border ${variantStyle} ${className}`}
        role="alert"
      >
        <span className="shrink-0 [&>svg]:size-5">{iconEl}</span>
        <div className="flex-1 min-w-0">
          {title && <p className="text-sm font-medium">{title}</p>}
          <div className={title ? 'text-xs text-content-secondary mt-0.5' : 'text-xs text-content-secondary'}>
            {children}
          </div>
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="shrink-0 p-1 rounded-lg hover:bg-surface-primary transition-colors text-content-faint hover:text-content-muted"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        )}
      </div>
    );
  },
);

Alert.displayName = 'Alert';

export default Alert;
